import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Clock, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SectionKicker } from "@/components/shared/SectionBits";
import { SERVICES } from "@/data/services";

export default function ContactPage() {
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            setDone(true);
            toast.success("Thank you. We'll be in touch shortly.");
            setForm({ name: "", company: "", email: "", phone: "", service: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="dr-container pt-32 pb-20">
                <div className="grid lg:grid-cols-12 gap-14 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-28">
                        <SectionKicker>Contact</SectionKicker>
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                            className="font-display font-bold text-dr-navy text-5xl md:text-6xl leading-[0.98]"
                        >
                            Confidential. Considered. And in your corner.
                        </motion.h1>
                        <p className="text-dr-dark/75 text-lg mt-6 leading-relaxed max-w-md">
                            Every enquiry is handled in confidence by a senior consultant. Share as much or as little
                            as you need, we&apos;ll take it from there.
                        </p>

                        <div className="mt-10 space-y-5">
                            {[
                                { icon: Mail, label: "Email", value: "contact@dynamicrisksgroup.com" },
                                { icon: Clock, label: "Hours", value: "Mon – Fri · 08:00 – 18:30 GMT · 24/7 incident line for retained clients" },
                            ].map((item) => (
                                <div key={item.label} className="flex gap-4">
                                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-dr-navy/5 text-dr-navy shrink-0">
                                        <item.icon className="w-4 h-4" />
                                    </span>
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.24em] text-dr-dark/60 font-medium">{item.label}</p>
                                        <p className="text-dr-navy font-medium mt-1">{item.value}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <a
                            href="https://linkedin.com"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-linkedin"
                            className="mt-10 hidden"
                        ></a>
                    </div>

                    <div className="lg:col-span-7">
                        <form
                            data-testid="contact-form"
                            onSubmit={submit}
                            className="bg-white border border-dr-silver rounded-3xl p-8 md:p-12"
                        >
                            {done && (
                                <div data-testid="contact-success" className="mb-8 p-5 rounded-xl bg-dr-navy text-white flex items-center gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-dr-blue" />
                                    <p className="font-medium">Thank you. A senior consultant will be in touch shortly.</p>
                                </div>
                            )}

                            <div className="grid md:grid-cols-2 gap-6">
                                <FieldInput label="Name" required id="name" testid="contact-input-name"
                                    value={form.name} onChange={(v) => update("name", v)} />
                                <FieldInput label="Company" id="company" testid="contact-input-company"
                                    value={form.company} onChange={(v) => update("company", v)} />
                                <FieldInput label="Email" type="email" required id="email" testid="contact-input-email"
                                    value={form.email} onChange={(v) => update("email", v)} />
                                <FieldInput label="Phone" id="phone" testid="contact-input-phone"
                                    value={form.phone} onChange={(v) => update("phone", v)} />
                                <div className="md:col-span-2">
                                    <Label>Service required</Label>
                                    <select
                                        data-testid="contact-input-service"
                                        value={form.service}
                                        onChange={(e) => update("service", e.target.value)}
                                        className="w-full pb-2.5 pt-3 bg-transparent border-0 border-b-2 border-dr-silver focus:border-dr-blue outline-none text-dr-navy"
                                    >
                                        <option value="">Select a service…</option>
                                        {SERVICES.map((s) => (
                                            <option key={s.slug} value={s.title}>{s.title}</option>
                                        ))}
                                        <option value="Other">Other / not sure</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <Label>Message</Label>
                                    <textarea
                                        data-testid="contact-input-message"
                                        required
                                        rows={5}
                                        minLength={10}
                                        value={form.message}
                                        onChange={(e) => update("message", e.target.value)}
                                        placeholder="Tell us about the challenge you're facing..."
                                        className="w-full pb-2.5 pt-3 bg-transparent border-0 border-b-2 border-dr-silver focus:border-dr-blue outline-none text-dr-navy resize-none"
                                    />
                                </div>
                            </div>

                            <p className="text-xs text-dr-dark/60 mt-6">
                                By submitting this form you agree that we may contact you about your enquiry. We treat all
                                enquiries in strict confidence.
                            </p>

                            <button
                                data-testid="contact-submit-btn"
                                disabled={submitting}
                                className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-dr-navy text-white font-semibold hover:bg-dr-blue disabled:opacity-60 transition-colors"
                            >
                                {submitting ? "Sending…" : "Send enquiry"}
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </form>
                    </div>
                </div>
            </section>

            {/* Map removed per client direction */}
        </div>
    );
}

function Label({ children }) {
    return <label className="text-xs uppercase tracking-[0.24em] text-dr-dark/60 font-medium">{children}</label>;
}

function FieldInput({ label, id, type = "text", required, value, onChange, testid }) {
    return (
        <div>
            <Label>{label}{required && " *"}</Label>
            <input
                id={id}
                data-testid={testid}
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full pb-2.5 pt-3 bg-transparent border-0 border-b-2 border-dr-silver focus:border-dr-blue outline-none text-dr-navy"
            />
        </div>
    );
}
